'use client';

import { useCallback, useSyncExternalStore } from 'react';

// Keyed by storage key.
// A write to one key only notifies hook instances watching that same key.
// Other `useSessionStorage` instances, watching a different key, are unaffected.
const listenersByKey = new Map<string, Set<() => void>>();

function subscribe(key: string, listener: () => void): () => void {
    let listeners = listenersByKey.get(key);
    if (!listeners) {
        listeners = new Set();
        listenersByKey.set(key, listeners);
    }
    listeners.add(listener);

    return () => {
        listeners.delete(listener);
        if (listeners.size === 0) {
            listenersByKey.delete(key);
        }
    };
}

function notify(key: string): void {
    listenersByKey.get(key)?.forEach((listener) => listener());
}

// sessionStorage access itself can throw synchronously.
// For example, a sandboxed iframe without `allow-same-origin`, or a restrictive storage policy, can block it.
// Every access here is guarded and treated as storage-unavailable rather than crashing the render.
function readSessionStorage(key: string): string | null {
    try {
        return sessionStorage.getItem(key);
    } catch {
        return null;
    }
}

function writeSessionStorage(key: string, next: string | null): void {
    try {
        if (next === null) {
            sessionStorage.removeItem(key);
        } else {
            sessionStorage.setItem(key, next);
        }
    } catch {
        // Storage is unavailable in this environment, so the write is silently dropped.
        // Callers already treat a null read as "no value," so this degrades to that same state instead of crashing.
    }
}

/**
 * Reads and writes a `sessionStorage` value, kept in sync across every mounted instance watching the same key.
 *
 * @param key - The sessionStorage key to read and write.
 * @param serverValue - The value to use on the server and during hydration, before sessionStorage is available.
 * @returns A tuple of the current value and a setter function.
 * The value is `null` when unset or when storage access fails.
 * Passing `null` to the setter removes the key.
 */
export function useSessionStorage(
    key: string,
    serverValue: string | null = null,
): [string | null, (newValue: string | null) => void] {
    const value = useSyncExternalStore(
        useCallback((listener: () => void) => subscribe(key, listener), [key]),
        () => readSessionStorage(key),
        // Server + hydration snapshot — the value React uses before the browser's storage is available to read from.
        () => serverValue,
    );

    const setValue = useCallback(
        (next: string | null) => {
            writeSessionStorage(key, next);
            notify(key);
        },
        [key],
    );

    return [value, setValue];
}
